import { KnownDeviceType } from "../../index.js";
import { toLoraEui } from "../../utils/shortCodes/EuiShortCodeMapper.js";
import { NodeIdString, UuidString, VendorOpaqueIdString } from "../../validators/index.js";
import type { DeviceIdentifierScheme, IdentifierSchemeConfiguration } from "./types.js";

const lowercase = (nodeId: string) => nodeId.toLowerCase();

/** An ID in the format printed on each device type's label, to show the user what to look for. */
const EXAMPLE_DEVICE_IDS: Partial<Record<KnownDeviceType, string>> = {
    gen4_block: "eui-fcc23d000042ee25",
    spr_2v: "eui-0004a30b00ea9e70",
    ePlantTreeTag: "A6075",
    draginoDL0S8N: "eui-a84041ffff287bd4",
    draginoSDI12LBLSNode: "A8404181005C67E0",
    rakWirelessRakGateway: "eui-ac1f09fffe2b79bb",
};

/** An example ID for the device type, or `undefined` when there is none to show. */
export const exampleManualDeviceId = (deviceType: KnownDeviceType): string | undefined =>
    EXAMPLE_DEVICE_IDS[deviceType];

/** Which side of the device its ID label is on, so the copy can point the user at it. */
export type LabelSide = "front" | "back" | "side";

/** Types omitted here have their label on the front. */
const LABEL_SIDES: Partial<Record<KnownDeviceType, LabelSide>> = {
    draginoDL0S8N: "back",
    rakWirelessRakGateway: "side",
};

export const getLabelSide = (deviceType: KnownDeviceType): LabelSide => LABEL_SIDES[deviceType] ?? "front";

/**
 * Per-scheme rules for validating and normalizing a device's `node_id`. Device types pick a scheme via
 * `DeviceTypeConfigurations[].identifierScheme`; this table is the shared behaviour for that scheme.
 */
export const IdentifierSchemeConfigurations: Record<DeviceIdentifierScheme, IdentifierSchemeConfiguration> = {
    // Hex is case-insensitive, but the stored form is lowercase so exact-match lookups and the
    // AssetIdentity unique index see one value per device.
    loraEui: {
        storedFormat: NodeIdString,
        normalizeInput: toLoraEui,
    },
    // UUIDs are case-insensitive (RFC 9562), but the stored form is lowercase
    uuid: {
        storedFormat: UuidString,
        normalizeInput: lowercase,
    },
    vendorOpaque: {
        storedFormat: VendorOpaqueIdString,
        normalizeInput: (nodeId) => nodeId,
    },
};
